import { ParticipantType } from '@entities/participants';
import type {
  BusAgentListPayload,
  BusAgentStatusListPayload,
  TeammateAvailabilityChangePayload,
} from '@main/event-listeners/payloads/bus-agent-list.payload';
import type {
  BusDialerCallStatusPayload,
  BusDialerSyncPayload,
  BusIncomingCallPayload,
} from '@main/event-listeners/payloads/bus-incoming-call.payload';
import type { BusUserAvailabilityChangedPayload } from '@main/event-listeners/payloads/bus-user-availability-changed.payload';
import type { IntegrationsettingUpdateReceivedPayload } from '@main/event-listeners/payloads/integration-setting-update-received.payload';
import { ControllerFactory } from '@main/factories/controller.factory';
import type { Logger } from '@main/utils/logger';
import { PhoneConnector } from '@main/sf-connector/scv-telephony-connector';

export type BusDialerEvent =
  | 'DIALER.INCOMING'
  | 'DIALER.SYNC'
  | 'DIALER.CALL_STATUS';

export type BusUserAvailabilityChangedEvent = 'USER.AVAILABILITY_CHANGED';
export type IntegrationSettingUpdateEvent =
  'SUBSCRIBE.INTEGRATION_SETTING_UPDATE_RECEIVED';

export interface BusEventData<T = unknown> {
  type:
    | BusDialerEvent
    | BusUserAvailabilityChangedEvent
    | IntegrationSettingUpdateEvent;
  payload: T;
}

export type BusEventMap = {
  'DIALER.INCOMING': (payload: BusIncomingCallPayload) => Promise<unknown>;
  'DIALER.SYNC': (payload: BusDialerSyncPayload) => Promise<unknown>;
  'DIALER.CALL_STATUS': (
    payload: BusDialerCallStatusPayload,
  ) => Promise<unknown>;
  'USER.AVAILABILITY_CHANGED': (
    payload: BusUserAvailabilityChangedPayload,
  ) => Promise<unknown>;
  'API.LOOKUP_TEAMMATES_FULFILLED': (
    payload: BusAgentListPayload,
  ) => Promise<unknown>;
  'API.FETCH_AVAILABILITIES_FULFILLED': (
    payload: BusAgentStatusListPayload,
  ) => Promise<unknown>;
  'USER.TEAMMATE_AVAILABILITY_CHANGED': (
    payload: TeammateAvailabilityChangePayload,
  ) => Promise<unknown>;
  'SUBSCRIBE.INTEGRATION_SETTING_UPDATE_RECEIVED': (
    payload: IntegrationsettingUpdateReceivedPayload,
  ) => Promise<unknown>;
};

export class BusEventListener {
  private eventMap: BusEventMap;
  // Tracks if voicemail options were hidden due to transfer
  private voicemailOptionsHidden = false;

  constructor(private logger: Logger) {
    this.eventMap = {
      'DIALER.INCOMING': this.incomingCallEventHandler,
      'DIALER.SYNC': this.dialerSyncEventHandler,
      'DIALER.CALL_STATUS': this.dialerCallStatusEventHandler,
      'USER.AVAILABILITY_CHANGED': this.userAvailabilityChangedEventHandler,
      'API.LOOKUP_TEAMMATES_FULFILLED': this.storeAgentListEventHandler,
      'API.FETCH_AVAILABILITIES_FULFILLED': this.syncAgentStatusEventHandler,
      'USER.TEAMMATE_AVAILABILITY_CHANGED':
        this.userTeammateAvailabilityChangedEventHandler,
      'SUBSCRIBE.INTEGRATION_SETTING_UPDATE_RECEIVED':
        this.integrationSettingUpdateReceivedEventHandler,
    };
  }

  private userTeammateAvailabilityChangedEventHandler = async (
    payload: TeammateAvailabilityChangePayload,
  ) => {
    const controller =
      ControllerFactory.buildTeammateAvailabilityChangedController();
    return controller.handle(payload);
  };
  private storeAgentListEventHandler = async (payload: BusAgentListPayload) => {
    const controller = ControllerFactory.buildStoreAgentListController(); // get data
    return controller.handle(payload);
  };

  private syncAgentStatusEventHandler = async (
    payload: BusAgentStatusListPayload,
  ) => {
    const controller = ControllerFactory.buildSyncAgentStatusController(); // get data
    return controller.handle(payload);
  };

  private userAvailabilityChangedEventHandler = async (
    payload: BusUserAvailabilityChangedPayload,
  ) => {
    const controller =
      ControllerFactory.buildStatusSyncAircallToCrmController();
    return controller.handle(payload);
  };

  private integrationSettingUpdateReceivedEventHandler = async (
    payload: IntegrationsettingUpdateReceivedPayload,
  ) => {
    const controller =
      ControllerFactory.buildSyncIntegrationSettingUpdateController();
    return controller.handle(payload);
  };

  private incomingCallEventHandler = async (
    payload: BusIncomingCallPayload,
  ) => {
    const controller = ControllerFactory.buildRingIncomingCallController();
    return controller.handle(payload);
  };

  private dialerSyncEventHandler = async (payload: BusDialerSyncPayload) => {
    const controller = ControllerFactory.buildSyncParticipantsController();
    return controller.handle({
      ...payload.metadata,
      callId: payload.metadata.callUuid,
    });
  };

  private dialerCallStatusEventHandler = async (
    payload: BusDialerCallStatusPayload,
  ) => {
    const { status } = payload;
    if (payload.flow === 'warmTransfer') {
      const prerecordedMessageController =
        ControllerFactory.buildFetchPrerecordedMessagesController();

      prerecordedMessageController.handleHideComponent();
      this.voicemailOptionsHidden = true;
      if (status === 'connected') {
        const controller =
          ControllerFactory.buildConnectedTransferCallController();
        const { callUuid, to, from } = payload;

        if (callUuid) {
          return controller.handle({
            callUuid,
            from,
            to,
          });
        } else {
          return controller.handle();
        }
      }
      if (status === 'ended' || status === 'hanging') {
        const controller =
          ControllerFactory.buildDisconnectedTransferCallController();
        return controller.handle();
      }
    } else if (payload.flow === 'regular') {
      if (status === 'connected') {
        if (!this.voicemailOptionsHidden) {
          const prerecordedMessageController =
            ControllerFactory.buildFetchPrerecordedMessagesController();
          prerecordedMessageController.handle().catch(console.error);
        }
        const controller = ControllerFactory.buildConnectedCallController();
        const transferCallController =
          ControllerFactory.buildDisconnectedTransferCallController();
        const holdcontroller = ControllerFactory.buildHoldToggleController();
        holdcontroller.handle({
          onHold: false,
          participantType: ParticipantType.EXTERNAL,
        });
        const { callUuid, to, from } = payload;
        transferCallController.handle();
        if (callUuid) {
          return controller.handle({
            callUuid,
            from,
            to,
          });
        } else {
          return controller.handle();
        }
      }
      if (status === 'ended') {
        this.voicemailOptionsHidden = false;
        const controller = ControllerFactory.buildDisconnectedCallController();
        return controller.handle();
      }
      if (status === 'hanging') {
        this.voicemailOptionsHidden = false;
      }
    }
    if (status === 'idle') {
      await new PhoneConnector().removeSupervisor();
      const controller = ControllerFactory.buildIdleCallController();
      return controller.handle();
    }
  };

  private eventDispatcher = async (event: MessageEvent<BusEventData>) => {
    const {
      data: { type, payload },
    } = event;

    console.log('eventDispatcher', { type, payload });

    const handler = this.eventMap[type];

    if (handler) {
      const loggerMessage = `Action: Bus event listener | Event: ${type}`;
      try {
        const response = await handler(<never>payload);
        if (response instanceof Error) {
          this.logger.error(loggerMessage, {
            payload,
            response,
          });
        } else if (!!response) {
          this.logger.info(loggerMessage, {
            payload,
            response,
          });
        }
      } catch (error) {
        this.logger.error(loggerMessage, {
          payload,
          error,
        });
      }
    }
  };

  addEventListener() {
    window.addEventListener('message', this.eventDispatcher);
  }
}
